import React, { useCallback, useMemo, useState } from 'react';
import EventTicketSetupModal from './EventTicketSetupModal';
import PlayguideLinkSuggestionNotice from './PlayguideLinkSuggestionNotice';
import EventDetailSectionIndex from './EventDetailSectionIndex';
import EventDetailsPrimarySections from './eventDetailsPrimarySections';
import EventDetailsGroupParticipants from './eventDetailsGroupParticipants';
import EventDetailsOperationalPanels from './eventDetailsOperationalPanels';
import EventDetailsResourceContractPanels from './eventDetailsResourceContractPanels';
import EventTaskDetailModal from './eventDetailsTaskDetailModal';
import EventDetailsAdminModals from './eventDetailsAdminModals';
import EventDetailsNotificationParticipantModals from './eventDetailsNotificationParticipantModals';
import { AutomaticTicketGuide } from './eventForm/AutomaticTicketSitePickerModal';

type Props = {
  eventId: string;
  isToday: boolean;
  canEditTicketSales: boolean;
  view: Record<string, unknown>;
  onTicketSetupSaved: () => Promise<unknown>;
};

export default function EventDetailsPageLayout({
  eventId,
  isToday,
  canEditTicketSales,
  view,
  onTicketSetupSaved,
}: Props) {
  const [ticketSetupOpen, setTicketSetupOpen] = useState(false);
  const [initialGuide, setInitialGuide] = useState<AutomaticTicketGuide | null>(null);
  const [suggestionRefreshKey, setSuggestionRefreshKey] = useState(0);
  const openTicketSetup = useCallback((guideCode: AutomaticTicketGuide | null = null) => {
    setInitialGuide(guideCode);
    setTicketSetupOpen(true);
  }, []);
  const closeTicketSetup = useCallback(() => {
    setTicketSetupOpen(false);
    setInitialGuide(null);
  }, []);
  const handleSaved = useCallback(async () => {
    await onTicketSetupSaved();
    setSuggestionRefreshKey((current) => current + 1);
  }, [onTicketSetupSaved]);
  const viewWithTicketSetup = useMemo(
    () => ({ ...view, openTicketSetup }),
    [openTicketSetup, view]
  );

  return (
    <>
      <EventDetailSectionIndex key={eventId} />
      <div className={`event-detail-container${isToday ? ' is-today' : ''}`}>
        <PlayguideLinkSuggestionNotice
          eventUuid={eventId}
          canEdit={canEditTicketSales}
          refreshKey={suggestionRefreshKey}
          onOpenSetup={openTicketSetup}
        />
        <EventDetailsPrimarySections view={viewWithTicketSetup} />
        <EventDetailsGroupParticipants view={viewWithTicketSetup} />
        <EventDetailsOperationalPanels view={viewWithTicketSetup} />
        <EventDetailsResourceContractPanels view={viewWithTicketSetup} />
        <EventTaskDetailModal view={viewWithTicketSetup} />
        <EventDetailsAdminModals view={viewWithTicketSetup} />
        <EventDetailsNotificationParticipantModals view={viewWithTicketSetup} />
        <EventTicketSetupModal
          open={ticketSetupOpen}
          eventUuid={eventId}
          initialAutomaticGuide={initialGuide}
          onClose={closeTicketSetup}
          onSaved={handleSaved}
        />
      </div>
    </>
  );
}
